import { isDev } from '@myre/common-conf';

import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  type OnChanges,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';

/**
 * Allows you to open an external link (href) or internal link (routerLink) based on the configuration.
 * Both methods cannot be used simultaneously.
 */
@Component({
  selector: 'myre-link',
  templateUrl: './link.component.html',
  styleUrls: ['./link.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLinkActive, RouterLink, NgTemplateOutlet],
})
export class LinkComponent implements OnChanges {
  @Input()
  public useLinkStyle = false;

  /**
   * Traditional web link to open in another browser tab
   */
  @Input()
  public externalLink: string;

  /**
   * Internal router link to open in the same browser tab : https://angular.io/api/router/RouterLink
   */
  @Input()
  public set routerLink(link: any[] | string) {
    this._routerLink = link;

    if (this._routerLinkActive) {
      (this._routerLinkActive as any).update();
    }
  }

  /**
   * Query params to add to the router link : https://angular.io/guide/router-tutorial-toh#query-parameters
   */
  @Input()
  public queryParams: Record<string, any> = {};

  /**
   * Options to pass to the routerLinkActive directive : https://angular.io/api/router/RouterLinkActive
   */
  @Input()
  public routerLinkActiveOptions: {
    exact: boolean;
  } = {
    exact: false,
  };

  /**
   * Defines the value of the aria for accessibility
   */
  @Input('aria-label') public ariaLabel: string;

  /**
   * Event emitter to emit when the link is active
   */
  @Output('isRouterLinkActive') public isRouterLinkActiveEmitter =
    new EventEmitter<boolean>();

  /**
   * @ignore
   */
  protected _routerLink: any[] | string;

  /**
   * @ignore
   */
  @ViewChild(RouterLinkActive, { static: false })
  private _routerLinkActive: RouterLinkActive;

  /**
   * @ignore
   */
  public ngOnChanges(): void {
    if (isDev() && this.externalLink && this._routerLink) {
      // eslint-disable-next-line no-console
      console.warn(
        'You cannot use both externalLink and routerLink at the same time',
      );
    }
  }
}
